import React from 'react';

import { Box, Paper, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { LightChip } from '@uplift/ui-v2';


const SIGNATURE_BLOCK_MAX_WIDTH = 380;


const useSignatureCardStyles = makeStyles<{ signed: boolean }>({ name: 'SignatureCard' })((theme, { signed }) => ({
  root: {
    marginTop: theme.spacing(3.5),
    padding: theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.75),
    scrollMarginBottom: theme.spacing(2.5),
    backgroundColor: signed ? theme.palette.success.background : theme.palette.grey[50],
    borderColor: signed ? theme.palette.success.border : undefined,
  },
  head: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
  },
}));


const useSignatureBlockStyles = makeStyles({ name: 'SignatureBlock' })(theme => ({
  root: {
    maxWidth: SIGNATURE_BLOCK_MAX_WIDTH,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  mark: {
    padding: theme.spacing(0.5, 0, 1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));


export const SignatureCard = React.forwardRef<HTMLDivElement, {
  title: string;
  chipLabel?: string;
  signed?: boolean;
  children?: React.ReactNode;
}>(({ title, chipLabel, signed = false, children }, ref) => {
  const { classes } = useSignatureCardStyles({ signed });

  return (
    <Paper variant="outlined" className={classes.root} ref={ref}>
      <Box className={classes.head}>
        <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
        {chipLabel && <LightChip size="small" color={signed ? 'success' : 'lightGrey'} label={chipLabel} />}
      </Box>
      {children}
    </Paper>
  );
});


export const SignatureBlock: React.VFC<{ name: string; label?: string }> = ({
  name,
  label = 'Signing as',
}) => {
  const { classes } = useSignatureBlockStyles();

  return (
    <Box className={classes.root}>
      <Typography variant="overline" color="secondary">{label}</Typography>
      <Typography variant="subtitle1" className={classes.mark}>{name}</Typography>
    </Box>
  );
};
